import { Controller, useFormContext } from "react-hook-form";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { Membership } from "@/types/system-setting/membership";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useGetIncomeCategorListQuery, useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";
import { useGetSalesTaxListQuery, useGetTaxGroupQuery } from "@/services/salesTaxApi";
import { RootState } from "@/redux/store";
import { Card } from "@/components/ui/card";
import Combobox from "@/components/ui/common/combobox";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { TaxGroupType } from "@/types/enums";
import { useGetPriceMastersQuery } from "@/services/priceMasterApi";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import PriceMasterForm from "../../price_master/modal/form";
import { PriceMaster } from "@/types/system-setting/priceMaster";

const PriceDiscountTaxForm = () => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const { data: incomeCategoryData } = useGetIncomeCategoryQuery({
    org_id: orgId, query: "status=active"
  });

  const { data: taxGroupData } = useGetTaxGroupQuery({
    query: "",
    org_id: orgId,
  });

  const taxGroupOptions = useMemo(() => {
    return Array.isArray(taxGroupData?.data)
      ? taxGroupData?.data.map((taxGroup) => ({
        label: taxGroup.name,
        value: String(taxGroup.id),
      }))
      : [];
  }, [taxGroupData]);

  const incomeCategoryOptions = useMemo(() => {
    return Array.isArray(incomeCategoryData?.data)
      ? incomeCategoryData?.data.map((incomeCategory) => ({
        label: incomeCategory.name,
        value: String(incomeCategory.id),
      }))
      : [];
  }, [incomeCategoryData]);

  // Fetch Price Master rules
  const { data: priceMasterData, isLoading: priceMasterLoading } = useGetPriceMastersQuery({
    query: "item_type=credit_add_ons",
    org_id: orgId,
  });

  const priceMasterOptions = useMemo(() => {
    return priceMasterData && Array.isArray(priceMasterData.data)
      ? priceMasterData.data.map((rule) => ({
        label: `${rule.rule_code} : ${rule.rule_name}`,
        subLabel: rule.rule_name,
        value: String(rule.id),
      }))
      : [];
  }, [priceMasterData]);

  // Preview State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedRuleData, setSelectedRuleData] = useState<PriceMaster | undefined>(undefined);

  const handlePreview = (ruleId: number) => {
    const rule = priceMasterData?.data?.find((r) => r.id === ruleId);
    if (rule) {
      setSelectedRuleData(rule);
      setPreviewOpen(true);
    }
  };

  const {
    control,
    formState: { errors },
    register,
    setValue,
    getValues,
    trigger,
    watch,
  } = useFormContext<Membership>();


  const [autoRenewal, setAutoRenewal] = useState<boolean | undefined>(
    (getValues("auto_renewal") as boolean) || false
  );

  useEffect(() => {
    const subscription = watch((value) => {
      setAutoRenewal(value?.auto_renewal);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleDayInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof Membership
  ) => {
    const value = parseInt(e.target.value, 10);
    if (value >= 15) {
      setValue(field, 15);
    }
  };



  return (
    <div className="text-black space-y-4">
      <Card className="p-4">
        <h1 className="font-semibold text-[#2D374] text-xl">
          Price, Discounts, and Tax Details
        </h1>
        <div className="grid grid-cols-3 gap-4 items-start mt-3">
          <div className="col-span-1 flex items-start gap-2">
            <Controller
              name="price_master_id"
              control={control}
              rules={{ required: "Required" }}
              render={({ field, fieldState: { error } }) => (
                <div className="flex-1">
                  <Combobox
                    list={priceMasterOptions}
                    defaultValue={priceMasterOptions.find((opt: any) => opt.value === String(field.value))?.label || ""}
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = priceMasterOptions.find((opt: any) => opt.label === selectedLabel);
                      field.onChange(selectedOption ? Number(selectedOption.value) : undefined);
                    }}
                    label="Price Master Rule"
                    disabled={priceMasterLoading}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </div>
              )}
            />
            <Controller
              name="price_master_id"
              control={control}
              render={({ field }) => (
                <>
                  {field.value && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 text-indigo-600 hover:bg-indigo-50 border border-indigo-100 shrink-0 mt-[1px]"
                      onClick={() => handlePreview(Number(field.value))}
                      title="Preview details"
                    >
                      <Eye className="h-5 w-5" />
                    </Button>
                  )}
                </>
              )}
            />
          </div>
          <Controller
            name="income_category_id"
            rules={{ required: "Required" }}
            control={control}
            render={({
              field: { onChange, value, onBlur },
              fieldState: { invalid, error },
            }) => (
              <div>
                <Combobox
                  list={incomeCategoryOptions}
                  defaultValue={incomeCategoryOptions.find(option => option.value === value?.toString())?.label || ""}
                  setFilter={(selectedLabel: string) => {
                    const selectedOption = incomeCategoryOptions.find(option => option.label === selectedLabel);
                    onChange(selectedOption ? Number(selectedOption.value) : undefined);
                  }}
                  label="Income Category"
                />
                {error && (
                  <span className="text-red-500 text-xs mt-[5px]">
                    {error.message}
                  </span>
                )}
              </div>
            )}
          />
          
          <Controller
            name="tax_group_id"
            rules={{ required: "Required" }}
            control={control}
            render={({
              field: { onChange, value, onBlur },
              fieldState: { invalid, error },
            }) => (
              <div>
                <Combobox
                  list={taxGroupOptions}
                  defaultValue={taxGroupOptions.find(option => option.value === value?.toString())?.label || ""}
                  setFilter={(selectedLabel: string) => {
                    const selectedOption = taxGroupOptions.find(option => option.label === selectedLabel);
                    onChange(selectedOption ? Number(selectedOption.value) : undefined);
                  }}
                  label="Tax Group"
                />
                {error && (
                  <span className="text-red-500 text-xs mt-[5px]">
                    {error.message}
                  </span>
                )}
              </div>
            )}
          />

        </div>
      </Card>

 {/* Price Master Preview Modal */}
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent className="overflow-y-auto max-w-full sm:max-w-[700px] w-full p-0">
          <SheetHeader className="p-6 border-b bg-indigo-50/50">
            <SheetTitle className="text-xl font-bold text-indigo-900">
              Pricing Rule Preview
            </SheetTitle>
            <SheetDescription className="text-indigo-600">
              Viewing pre-configured pricing rule details
            </SheetDescription>
          </SheetHeader>

          <div className="p-6">
            {selectedRuleData && (
              <PriceMasterForm
                isOpen={previewOpen}
                setOpen={setPreviewOpen}
                action="edit"
                data={selectedRuleData}
                readOnly={true}
                setData={() => { }}
                setAction={() => { }}
                refetch={() => { }}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default PriceDiscountTaxForm;